/**
 * Offline queue for counter submissions.
 *
 * Strategy
 * ──────────
 * - Counter submits a count: we always try the API first.
 * - If the request fails due to network (offline / server unreachable), we
 *   enqueue the submission in localStorage and mark the UI as "pending sync".
 * - When the browser reports `online`, or on load, or on a timer, we flush
 *   the queue, one item at a time, FIFO.
 * - A lightweight optimistic cache keeps UI responsive while offline.
 */

import axios from "axios";
import { API } from "./api";

const QUEUE_KEY = "irs_offline_queue_v1";
const CACHE_KEY = (sessionId) => `irs_counter_cache_v1:${sessionId}`;

export function readQueue() {
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]"); }
  catch { return []; }
}

function writeQueue(q) {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
  window.dispatchEvent(new CustomEvent("irs:queue", { detail: { size: q.length } }));
}

export function enqueueSubmission(entry) {
  // entry: { session_id, item_id, counted_quantity, client_id, submitted_at }
  const q = readQueue();
  q.push(entry);
  writeQueue(q);
}

export function queueSize() {
  return readQueue().length;
}

export function clearQueue() {
  writeQueue([]);
}

export function cacheCounterItems(sessionId, data) {
  try { localStorage.setItem(CACHE_KEY(sessionId), JSON.stringify({ data, cached_at: Date.now() })); }
  catch {}
}

export function loadCachedCounterItems(sessionId) {
  try {
    const raw = localStorage.getItem(CACHE_KEY(sessionId));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

/**
 * Apply an optimistic local update to the counter cache for responsive UI.
 * Mutates the cached snapshot's item with incremented attempts and new count.
 */
export function applyLocalOptimistic(sessionId, itemId, countedQuantity, remarks = "") {
  const cached = loadCachedCounterItems(sessionId);
  if (!cached?.data?.items) return;
  const item = cached.data.items.find((i) => i.id === itemId);
  if (!item) return;
  item.my_latest_count = countedQuantity;
  item.my_remarks = remarks;
  item.attempts_used = (item.attempts_used || 0) + 1;
  item.attempts_remaining = Math.max(0, 5 - item.attempts_used);
  item.pending_sync = true;
  cacheCounterItems(sessionId, cached.data);
}

/** Flush as many queued submissions as possible. Returns {synced, failed, remaining}. */
export async function flushQueue() {
  const q = readQueue();
  if (!q.length) return { synced: 0, failed: 0, remaining: 0 };
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return { synced: 0, failed: 0, remaining: q.length };
  }
  const token = localStorage.getItem("irs_token");
  if (!token) return { synced: 0, failed: 0, remaining: q.length };

  const remaining = [];
  let synced = 0, failed = 0;
  for (const entry of q) {
    try {
      await axios.post(
        `${API}/counter/sessions/${entry.session_id}/items/${entry.item_id}/submit`,
        {
          counted_quantity: entry.counted_quantity,
          warehouse_partner_name: entry.warehouse_partner_name || "",
          submitted_location: entry.submitted_location || "",
          remarks: entry.remarks || "",
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 },
      );
      synced++;
    } catch (err) {
      // 4xx (business rule errors) → drop. 5xx or network → retry.
      const status = err?.response?.status;
      if (status && status >= 400 && status < 500) {
        // Drop — e.g. attempts exhausted, item locked, cutoff
        failed++;
      } else {
        remaining.push(entry);
      }
    }
  }
  writeQueue(remaining);
  return { synced, failed, remaining: remaining.length };
}

let _flushTimer = null;
export function startBackgroundSync() {
  if (_flushTimer) return;
  const tryFlush = () => flushQueue().catch(() => {});
  window.addEventListener("online", tryFlush);
  _flushTimer = setInterval(tryFlush, 15000);
  // First attempt shortly after boot
  setTimeout(tryFlush, 1500);
}
